import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Box, Button, FormControlLabel, Switch, TextField, Typography, useMediaQuery } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { WizardAccordion } from '../../components/WizardAccordion';
import { ImageThumbnail } from '../../components/ImageThumbnail';
import { DynamicTextFields } from '../../components/DynamicTextFields';
import { DocumentsSection } from '../../components/DocumentsSection';
import { FavoritesRail } from '../../components/FavoritesRail';
import { FavoriteStarButton } from '../../components/edit-object/FavoriteStarButton';
import { ValuationRows } from '../../components/edit-object/ValuationRows';
import { useCatalog } from '../../hooks/useCatalog';
import { useUpload } from '../../hooks/useUpload';
import { useWizardCompletion } from '../../hooks/useWizardCompletion';
import { useAutosaveStatus } from '../../hooks/useAutosaveStatus';
import { useRegisterSaveNow } from '../../hooks/useRegisterSaveNow';
import { logger } from '../../utils/logger';
import { en } from '../../translations/en';
import type { Valuation } from '../../contexts/CatalogContext';

interface AdditionalInfoTabProps {
  catalogItemId?: string;
  ownerId?: string | null;
  /**
   * When true, hides the "Additional Notes - Public" accordion. Used by
   * the redesigned `PrivateOnlyStep` where public notes don't belong on
   * a page banner-titled "These details are never shown publicly".
   * The data store still loads/saves correctly — only the UI is hidden.
   */
  hidePublicNotes?: boolean;
  /**
   * When true, the Internal Only Data mega-accordion is replaced by five
   * focused sections that match the Private Only redesign (image #7):
   * Current Location, Private Notes, Valuation, Documents, and
   * Condition / Conservation Notes. The single-accordion layout used by
   * the legacy upload wizard remains the default.
   */
  splitPrivateSections?: boolean;
}

export const AdditionalInfoTab = ({ catalogItemId, ownerId, hidePublicNotes, splitPrivateSections }: AdditionalInfoTabProps) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { currentItem, saveAdditionalInfo, getAdditionalInfo } = useCatalog();
  const { catalogId } = useUpload();
  const effectiveCatalogId = catalogItemId || catalogId || currentItem?.catalogItemId;
  const { markStepComplete } = useWizardCompletion();
  const { setStatus, setLastSaveTime } = useAutosaveStatus();

  const saveTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const loadedCatalogIdRef = useRef<string | null>(null);
  // Autosave guard — engaged from first render to protect against
  // debounce/unmount-flush firing before load completes (sibling regression
  // of DA-55L.2499). Complementary to hasPendingChangesRef (which only
  // gates the unmount path).
  const [isLoadingInitialData, setIsLoadingInitialData] = useState(true);
  const isLoadingInitialDataRef = useRef(true);
  useEffect(() => { isLoadingInitialDataRef.current = isLoadingInitialData; }, [isLoadingInitialData]);

  // Load-failure guard — mirrors DescribeTab.hasError (GL#40).
  // When the initial GET fails, we hold all write paths blocked so that a
  // subsequent debounce or unmount-flush cannot write blank defaults over the
  // persisted row.
  const [hasError, setHasError] = useState(false);
  const hasErrorRef = useRef(false);
  useEffect(() => { hasErrorRef.current = hasError; }, [hasError]);
  const [publicNotes, setPublicNotes] = useState<string[]>([]);
  const [privateNotes, setPrivateNotes] = useState<string[]>([]);
  const [physicalLocation, setPhysicalLocation] = useState('');
  const [estimatedValue, setEstimatedValue] = useState('');
  const [valuations, setValuations] = useState<Valuation[]>([]);
  const [markedAsSold, setMarkedAsSold] = useState(false);
  const [conditionNotes, setConditionNotes] = useState('');

  // Refs tracking latest state for use in unmount cleanup (avoids stale closures)
  const publicNotesRef = useRef<string[]>([]);
  const privateNotesRef = useRef<string[]>([]);
  const physicalLocationRef = useRef<string>('');
  const estimatedValueRef = useRef<string>('');
  const valuationsRef = useRef<Valuation[]>([]);
  const markedAsSoldRef = useRef<boolean>(false);
  const conditionNotesRef = useRef<string>('');
  const effectiveCatalogIdRef = useRef<string | undefined>(undefined);

  // Refs to track preserved fields (set during each save to avoid API call on unmount)
  const savedRelatedWorksRef = useRef<unknown[]>([]);
  const savedExhibitionsRef = useRef<unknown[]>([]);
  const savedStepNotesRef = useRef<Record<string, string[]>>({});

  // Track last saved payload to avoid duplicate unmount saves
  const lastSavedPayloadRef = useRef<string>('');
  // Track whether there are unsaved changes pending
  const hasPendingChangesRef = useRef<boolean>(false);
  // Track ownerId for unmount cleanup (avoids stale closure)
  const ownerIdRef = useRef<string | null | undefined>(ownerId);

  // Keep state refs in sync with latest state values
  useEffect(() => { publicNotesRef.current = publicNotes; }, [publicNotes]);
  useEffect(() => { privateNotesRef.current = privateNotes; }, [privateNotes]);
  useEffect(() => { physicalLocationRef.current = physicalLocation; }, [physicalLocation]);
  useEffect(() => { estimatedValueRef.current = estimatedValue; }, [estimatedValue]);
  useEffect(() => { valuationsRef.current = valuations; }, [valuations]);
  useEffect(() => { markedAsSoldRef.current = markedAsSold; }, [markedAsSold]);
  useEffect(() => { conditionNotesRef.current = conditionNotes; }, [conditionNotes]);
  useEffect(() => { effectiveCatalogIdRef.current = effectiveCatalogId; }, [effectiveCatalogId]);
  useEffect(() => { ownerIdRef.current = ownerId; }, [ownerId]);

  // Load existing data on mount or when catalogId changes
  useEffect(() => {
    // Only load if we haven't already loaded this specific catalogId
    if (effectiveCatalogId && loadedCatalogIdRef.current !== effectiveCatalogId) {
      loadedCatalogIdRef.current = effectiveCatalogId;
      setIsLoadingInitialData(true);
      getAdditionalInfo(effectiveCatalogId, ownerId).then((data) => {
        setPublicNotes(data.publicNotes || []);
        setPrivateNotes(data.privateNotes || []);
        setPhysicalLocation(data.physicalLocation || '');
        setEstimatedValue(data.estimatedValue || '');
        // Filter out legacy malformed entries (require value/currency/type strings)
        // so we don't round-trip a Valuation with missing fields back to the
        // backend on the next save.
        setValuations(
          Array.isArray(data.valuations)
            ? (data.valuations as unknown[]).filter(
                (v): v is Valuation =>
                  typeof v === 'object' &&
                  v !== null &&
                  typeof (v as { value?: unknown }).value === 'string' &&
                  typeof (v as { currency?: unknown }).currency === 'string' &&
                  typeof (v as { type?: unknown }).type === 'string',
              )
            : [],
        );
        setMarkedAsSold(Boolean(data.markedAsSold));
        setConditionNotes(data.conditionNotes || '');

        // Cache preserved fields from initial load
        savedRelatedWorksRef.current = Array.isArray(data.relatedWorks) ? data.relatedWorks : [];
        savedExhibitionsRef.current = Array.isArray(data.exhibitions) ? data.exhibitions : [];
        savedStepNotesRef.current = (data.stepNotes && typeof data.stepNotes === 'object' ? data.stepNotes : {}) as Record<string, string[]>;

        // Initialize last saved payload to prevent false "pending changes" on first load
        lastSavedPayloadRef.current = JSON.stringify({
          publicNotes: data.publicNotes || [],
          privateNotes: data.privateNotes || [],
          physicalLocation: data.physicalLocation || '',
          estimatedValue: data.estimatedValue || '',
          valuations: data.valuations || [],
          markedAsSold: Boolean(data.markedAsSold),
          conditionNotes: data.conditionNotes || '',
        });
        hasPendingChangesRef.current = false;

        // Update completion tracking after data loads.
        // Step 0: Notes (public or private); step 1: Location and Value (#1082).
        const hasPublicNotes = (data.publicNotes || []).some((note: string) => note.trim() !== '');
        const hasPrivateNotesOnLoad = (data.privateNotes || []).some((note: string) => note.trim() !== '');
        markStepComplete('additionalInfo', 0, hasPublicNotes || hasPrivateNotesOnLoad);

        const hasLocationOrValue = (data.physicalLocation || '').trim() !== '' || (data.estimatedValue || '').trim() !== '';
        markStepComplete('additionalInfo', 1, hasLocationOrValue);

      }).then(() => {
        // Success path: release the guard.
        setIsLoadingInitialData(false);
      }).catch((err) => {
        // Load failed — set hasError so all write paths stay blocked (GL#40).
        logger.error('AdditionalInfoTab: Failed to load additional info', err);
        setHasError(true);
        setIsLoadingInitialData(false);
      });
    } else if (!effectiveCatalogId) {
      setIsLoadingInitialData(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [effectiveCatalogId]);

  // Completion tracking function.
  // Step 0: Notes (public or private); step 1: Location and Value (#1082).
  const updateCompletionTracking = useCallback(() => {
    const hasPublicNotes = publicNotes.some(note => note.trim() !== '');
    const hasPrivateNotes = privateNotes.some(note => note.trim() !== '');
    markStepComplete('additionalInfo', 0, hasPublicNotes || hasPrivateNotes);

    const hasLocationOrValue = physicalLocation.trim() !== '' || estimatedValue.trim() !== '';
    markStepComplete('additionalInfo', 1, hasLocationOrValue);
  }, [markStepComplete, publicNotes, privateNotes, physicalLocation, estimatedValue]);

  // Auto-save function — preserves exhibitions and stepNotes owned by ProvenanceTab
  const saveData = useCallback(async () => {
    // Guard: never save during initial load OR after a load failure (GL#40).
    if (!effectiveCatalogId || isLoadingInitialData || hasError) return;
    setStatus('saving');
    try {
      // Fetch existing data to preserve relatedWorks, exhibitions and stepNotes saved by ProvenanceTab
      let existingRelatedWorks: unknown[] = savedRelatedWorksRef.current;
      let existingExhibitions: unknown[] = savedExhibitionsRef.current;
      let existingStepNotes: Record<string, string[]> = savedStepNotesRef.current;
      try {
        const existing = await getAdditionalInfo(effectiveCatalogId, ownerId);
        const { userId: _u, catalogItemId: _c, updatedAt: _t, ...data } =
          existing as Record<string, unknown>;
        existingRelatedWorks = (Array.isArray(data.relatedWorks) ? data.relatedWorks : []) as unknown[];
        existingExhibitions = (Array.isArray(data.exhibitions) ? data.exhibitions : []) as unknown[];
        existingStepNotes = (data.stepNotes && typeof data.stepNotes === 'object' ? data.stepNotes : {}) as Record<string, string[]>;

        // Update cached preserved fields for unmount flush
        savedRelatedWorksRef.current = existingRelatedWorks;
        savedExhibitionsRef.current = existingExhibitions;
        savedStepNotesRef.current = existingStepNotes;
      } catch (err: unknown) {
        const axiosErr = err as { response?: { status?: number } };
        if (axiosErr.response?.status !== 404) throw err;
      }

      await saveAdditionalInfo(effectiveCatalogId, {
        relatedWorks: existingRelatedWorks,
        publicNotes,
        privateNotes,
        physicalLocation,
        estimatedValue,
        valuations,
        markedAsSold,
        conditionNotes,
        exhibitions: existingExhibitions,
        stepNotes: existingStepNotes
      }, ownerId);

      // Update last saved payload and clear pending flag
      lastSavedPayloadRef.current = JSON.stringify({
        publicNotes,
        privateNotes,
        physicalLocation,
        estimatedValue,
        valuations,
        markedAsSold,
        conditionNotes,
      });
      hasPendingChangesRef.current = false;

      // Update completion tracking
      updateCompletionTracking();

      setStatus('saved');
      setLastSaveTime(new Date());
    } catch (err) {
      setStatus('error');
      logger.error('Failed to save additional info', err);
    }
  }, [effectiveCatalogId, isLoadingInitialData, hasError, saveAdditionalInfo, getAdditionalInfo, publicNotes, privateNotes, physicalLocation, estimatedValue, valuations, markedAsSold, conditionNotes, updateCompletionTracking, setStatus, setLastSaveTime]);

  const saveDataRef = useRef(saveData);
  useEffect(() => { saveDataRef.current = saveData; }, [saveData]);
  // Let the wizard "Finished" button flush this tab's pending save.
  useRegisterSaveNow(saveDataRef, saveTimeoutRef);

  const debouncedSave = useCallback(() => {
    hasPendingChangesRef.current = true;
    // Signal immediately that there are unsaved edits — clears "All changes saved" (GL#40 Part 1).
    setStatus('dirty');
    if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current);
    saveTimeoutRef.current = setTimeout(() => saveDataRef.current(), 2000);
  }, [setStatus]); // setStatus is stable from context

  // Cleanup timeouts and flush pending saves on unmount
  // Uses refs for latest state values to avoid stale closure problems
  useEffect(() => {
    // Capture stable ref references for cleanup
    const saveTimeout = saveTimeoutRef;
    const catalogIdRef = effectiveCatalogIdRef;

    return () => {
      // Cancel pending debounce timer
      if (saveTimeout.current) {
        clearTimeout(saveTimeout.current);
        saveTimeout.current = null;
      }

      // Flush pending save on unmount if there are unsaved changes
      // This ensures reordered notes (and other edits) are not lost when
      // the user navigates away before the 2-second debounce fires.
      // Never flush during initial load or after load failure — would wipe row (GL#40).
      const currentCatalogId = catalogIdRef.current;
      if (currentCatalogId && hasPendingChangesRef.current && !isLoadingInitialDataRef.current && !hasErrorRef.current) {
        const currentPublicNotes = publicNotesRef.current;
        const currentPrivateNotes = privateNotesRef.current;
        const currentPhysicalLocation = physicalLocationRef.current;
        const currentEstimatedValue = estimatedValueRef.current;
        const currentValuations = valuationsRef.current;
        const currentMarkedAsSold = markedAsSoldRef.current;
        const currentConditionNotes = conditionNotesRef.current;

        // Only save if data actually changed (compare against last saved payload)
        const currentPayload = JSON.stringify({
          publicNotes: currentPublicNotes,
          privateNotes: currentPrivateNotes,
          physicalLocation: currentPhysicalLocation,
          estimatedValue: currentEstimatedValue,
          valuations: currentValuations,
          markedAsSold: currentMarkedAsSold,
          conditionNotes: currentConditionNotes,
        });

        if (currentPayload !== lastSavedPayloadRef.current) {
          saveAdditionalInfo(currentCatalogId, {
            relatedWorks: savedRelatedWorksRef.current,
            publicNotes: currentPublicNotes,
            privateNotes: currentPrivateNotes,
            physicalLocation: currentPhysicalLocation,
            estimatedValue: currentEstimatedValue,
            valuations: currentValuations,
            markedAsSold: currentMarkedAsSold,
            conditionNotes: currentConditionNotes,
            exhibitions: savedExhibitionsRef.current,
            stepNotes: savedStepNotesRef.current,
          }, ownerIdRef.current).catch((err) => {
            logger.error('Failed to flush additional info save on unmount', err);
          });
        }
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Retry effect: when hasError is cleared and loadedCatalogIdRef is null,
  // re-run the load (mirrors DescribeTab's retry pattern — GL#40).
  useEffect(() => {
    if (!hasError && loadedCatalogIdRef.current === null && effectiveCatalogId) {
      loadedCatalogIdRef.current = effectiveCatalogId;
      setIsLoadingInitialData(true);
      getAdditionalInfo(effectiveCatalogId, ownerId).then((data) => {
        setPublicNotes(data.publicNotes || []);
        setPrivateNotes(data.privateNotes || []);
        setPhysicalLocation(data.physicalLocation || '');
        setEstimatedValue(data.estimatedValue || '');
        setValuations(
          Array.isArray(data.valuations)
            ? (data.valuations as unknown[]).filter(
                (v): v is Valuation =>
                  typeof v === 'object' &&
                  v !== null &&
                  typeof (v as { value?: unknown }).value === 'string' &&
                  typeof (v as { currency?: unknown }).currency === 'string' &&
                  typeof (v as { type?: unknown }).type === 'string',
              )
            : [],
        );
        setMarkedAsSold(Boolean(data.markedAsSold));
        setConditionNotes(data.conditionNotes || '');
        savedRelatedWorksRef.current = Array.isArray(data.relatedWorks) ? data.relatedWorks : [];
        savedExhibitionsRef.current = Array.isArray(data.exhibitions) ? data.exhibitions : [];
        savedStepNotesRef.current = (data.stepNotes && typeof data.stepNotes === 'object' ? data.stepNotes : {}) as Record<string, string[]>;
        lastSavedPayloadRef.current = JSON.stringify({
          publicNotes: data.publicNotes || [],
          privateNotes: data.privateNotes || [],
          physicalLocation: data.physicalLocation || '',
          estimatedValue: data.estimatedValue || '',
          valuations: data.valuations || [],
          markedAsSold: Boolean(data.markedAsSold),
          conditionNotes: data.conditionNotes || '',
        });
        hasPendingChangesRef.current = false;
        const hasPublicNotes = (data.publicNotes || []).some((note: string) => note.trim() !== '');
        const hasPrivateNotesOnLoad = (data.privateNotes || []).some((note: string) => note.trim() !== '');
        markStepComplete('additionalInfo', 0, hasPublicNotes || hasPrivateNotesOnLoad);
        const hasLocationOrValue = (data.physicalLocation || '').trim() !== '' || (data.estimatedValue || '').trim() !== '';
        markStepComplete('additionalInfo', 1, hasLocationOrValue);
      }).then(() => {
        setIsLoadingInitialData(false);
      }).catch((err) => {
        logger.error('AdditionalInfoTab: Failed to load additional info on retry', err);
        setHasError(true);
        setIsLoadingInitialData(false);
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasError, effectiveCatalogId]);

  if (hasError) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Typography variant="h6" color="error">
          Something went wrong loading the additional info form.
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          Please try again or contact support if the issue persists.
        </Typography>
        <Button
          variant="outlined"
          sx={{ mt: 2 }}
          onClick={() => {
            setHasError(false);
            loadedCatalogIdRef.current = null;
          }}
        >
          Retry
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <ImageThumbnail />

      {!hidePublicNotes && (
        <WizardAccordion
          id="additional-notes-public"
          title={en.wizard.additionalInfo.additionalNotesPublic.title}
          subtitle={en.wizard.additionalInfo.additionalNotesPublic.subtitle}
          defaultExpanded={true}
          privacyFieldPath="additionalInfo.publicNotes"
        >
          <DynamicTextFields
            label={en.wizard.additionalInfo.additionalNotesPublic.label}
            addButtonText={en.wizard.additionalInfo.additionalNotesPublic.addButton}
            initialValues={publicNotes}
            onChange={(values) => {
              setPublicNotes(values);
              debouncedSave();
            }}
          />
        </WizardAccordion>
      )}

      {!splitPrivateSections && (
        <WizardAccordion
          id="additional-internal-only"
          title={en.wizard.additionalInfo.internalOnlyData.title}
          subtitle={en.wizard.additionalInfo.internalOnlyData.subtitle}
          defaultExpanded={true}
          alwaysPrivate
        >
          <Box sx={{ mb: 3 }}>
            <DynamicTextFields
              label={en.wizard.additionalInfo.internalOnlyData.privateNotesLabel}
              addButtonText={en.wizard.additionalInfo.internalOnlyData.privateNotesAddButton}
              initialValues={privateNotes}
              onChange={(values) => {
                setPrivateNotes(values);
                debouncedSave();
              }}
            />
          </Box>
          <Box sx={{ flexDirection: 'column' }}>
            <FavoritesRail
              type="locations"
              currentValue={physicalLocation}
              onPick={(value) => {
                setPhysicalLocation(value);
                debouncedSave();
              }}
            />
          </Box>
          <Box sx={{ display: 'flex', gap: 2, flexDirection: isMobile ? 'column' : 'row' }}>
            <TextField
              fullWidth
              label={en.wizard.additionalInfo.internalOnlyData.locationLabel}
              variant="outlined"
              value={physicalLocation}
              onChange={(e) => {
                setPhysicalLocation(e.target.value);
                debouncedSave();
              }}
            />
            <TextField
              fullWidth
              label={en.wizard.additionalInfo.internalOnlyData.valueLabel}
              variant="outlined"
              value={estimatedValue}
              onChange={(e) => {
                setEstimatedValue(e.target.value);
                debouncedSave();
              }}
            />
          </Box>
        </WizardAccordion>
      )}

      {splitPrivateSections && (
        <>
          <WizardAccordion
            id="private-current-location"
            title="Current Location"
            subtitle="Where this object physically lives — studio, gallery, storage, on loan, etc."
            defaultExpanded
            alwaysPrivate
            summary={physicalLocation || undefined}
            headerSummary={physicalLocation || undefined}
          >
            <FavoritesRail
              type="locations"
              currentValue={physicalLocation}
              hideStar
              onPick={(value) => {
                setPhysicalLocation(value);
                debouncedSave();
              }}
            />
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <TextField
                fullWidth
                label="Location"
                placeholder="e.g. Studio — Brooklyn NY"
                variant="outlined"
                value={physicalLocation}
                onChange={(e) => {
                  setPhysicalLocation(e.target.value);
                  debouncedSave();
                }}
              />
              <FavoriteStarButton type="locations" currentValue={physicalLocation} />
            </Box>
          </WizardAccordion>

          <WizardAccordion
            id="private-notes"
            title="Private Notes"
            subtitle="Visible only to you and your delegates."
            defaultExpanded
            alwaysPrivate
          >
            <DynamicTextFields
              label="Notes"
              addButtonText="Add note"
              placeholder="Personal reflections, process notes, stories behind the work, anything not for public view…"
              initialValues={privateNotes}
              onChange={(values) => {
                setPrivateNotes(values);
                debouncedSave();
              }}
            />
          </WizardAccordion>

          <WizardAccordion
            id="private-valuation"
            title="Valuation"
            subtitle="Sales price, appraisals, insurance, or custom valuations."
            defaultExpanded
            alwaysPrivate
            headerSummary={
              valuations.length > 0
                ? `${valuations.length} valuation${valuations.length === 1 ? '' : 's'}`
                : undefined
            }
          >
            <FormControlLabel
              sx={{ mb: 2 }}
              control={
                <Switch
                  checked={markedAsSold}
                  onChange={(e) => {
                    setMarkedAsSold(e.target.checked);
                    debouncedSave();
                  }}
                />
              }
              label={<Typography variant="body2" sx={{ fontWeight: 500 }}>Mark as sold</Typography>}
            />
            <ValuationRows
              valuations={valuations}
              onChange={(next) => {
                setValuations(next);
                debouncedSave();
              }}
            />
          </WizardAccordion>

          <WizardAccordion
            id="private-documents"
            title="Documents"
            subtitle="Always-private supporting documents — appraisals, receipts, certificates, etc."
            defaultExpanded={false}
            alwaysPrivate
          >
            <DocumentsSection catalogId={effectiveCatalogId ?? null} ownerId={ownerId ?? undefined} />
          </WizardAccordion>

          <WizardAccordion
            id="private-condition-notes"
            title="Condition / Conservation Notes"
            subtitle="Damage, repairs, environmental exposure, or assessment history."
            defaultExpanded
            alwaysPrivate
            headerSummary={
              conditionNotes.trim()
                ? `${conditionNotes.trim().slice(0, 80)}${conditionNotes.trim().length > 80 ? '…' : ''}`
                : undefined
            }
          >
            <TextField
              fullWidth
              multiline
              rows={3}
              label="Notes"
              placeholder="Describe condition, damage, wear, repairs, conservation work, environmental exposure, or assessment history…"
              variant="outlined"
              value={conditionNotes}
              onChange={(e) => {
                setConditionNotes(e.target.value);
                debouncedSave();
              }}
            />
          </WizardAccordion>
        </>
      )}

      {!splitPrivateSections && (
        <WizardAccordion
          id="additional-info-documents"
          title="Documents"
          subtitle="Always-private supporting documents — appraisals, receipts, certificates, etc."
          defaultExpanded={false}
        >
          <DocumentsSection catalogId={effectiveCatalogId ?? null} ownerId={ownerId ?? undefined} />
        </WizardAccordion>
      )}
    </Box>
  );
};
